import { FC, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Tooltip } from '@mui/material';
import { DateRange, EventBusy } from '@mui/icons-material';

import { CustomAlert, CustomButton } from '../../../shared/uiComponents';
import { useRefreshProviderCancellations, useScreenWidth } from '../../../shared/Helpers/hooks';
import RequestCancellationModal from '../../Schedules/RequestTimeOffModal';
import { AdminTypes } from '../../../components/Menu';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { getEvents } from '../../../redux/State/clientSlice/calendarSlice';
import { getPendingRequests } from '../../../redux/State/clientSlice/scheduleCancellationSlice';

const RequestCancellation = () => {
	const [open, setOpen] = useState(false);
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth < 768;

	const role = useSelector((state) => state.account.role);
	const isAdmin = role?.section.id === AdminTypes.admin;
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const filterData = useSelector((state) => state.calendarHelper.filterData);
	const canFilterEvents = isAdmin || role?.section.id === AdminTypes.cs;
	const refreshCancellations = useRefreshProviderCancellations();

	const handleSuccess = useCallback(() => {
		setOpen(false);
		const eventData = canFilterEvents
			? { days, clientId: filterData.client?.id, userId: filterData.therapist?.id, includeBTEvents: filterData.includeBTEvents, includeBCBAEvents: filterData.includeBCBAEvents }
			: { days, includeBTEvents: filterData.includeBTEvents, includeBCBAEvents: filterData.includeBCBAEvents };
		dispatch(getEvents(eventData));
		dispatch(getPendingRequests(undefined));
		refreshCancellations();
	}, [dispatch, days, filterData, canFilterEvents, refreshCancellations]);

	const RequestCancellationContent: FC<{ onClose: () => void }> = useCallback(
		({ onClose }) => <RequestCancellationModal onClose={onClose} onSuccess={handleSuccess} />,
		[handleSuccess],
	);

	if (isAdmin) return null;

	return (
		<>
			{isMobile ? (
				<Tooltip title="Request Cancellation" arrow>
					<div>
						<CustomButton
							iconOnly={<EventBusy style={{ fontSize: 18 }} />}
							buttonType="secondaryGray"
							onClick={() => setOpen(true)}
							aria-label="Request Cancellation"
						/>
					</div>
				</Tooltip>
			) : (
				<CustomButton
					title="Request Cancellation"
					buttonType="secondaryGray"
					onClick={() => setOpen(true)}
					className="requestCancellationButton"
				/>
			)}
			<CustomAlert
				open={open}
				onClose={() => setOpen(false)}
				title="Request Cancellation"
				Icon={({ color }) => <DateRange htmlColor={color || '#1e3a5f'} />}
				compactHeaderIcon
				iconBackgroundColor="transparent"
				maxWidth="sm"
				disableBackdropClose
				Content={RequestCancellationContent}
			/>
		</>
	);
};

export default RequestCancellation;
